import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import camelCase from 'camelcase'
import { underlineToDash } from '../../../../utils/text/text-utils'
import { InputLabel } from '../../display/labelText/LabelText'
import { ContentStateProperty } from '../../../../@types/types'
import { useFormState } from '../../../pages/insertionPage/wizard/useFormState'
import { IconS24CrossCircle24Filled } from '@is24/cosma-ui-icons'

const KEY_ENTER = 'Enter'
const KEY_SPACE = 'Space'

export type ChipOptionFieldProps = {
  id: string
  propertyName?: ContentStateProperty
  option?: string
  options: string[]
  translation: string
  errors?: string[]
  enableDeselection?: boolean
  isFirstChipFiltered?: boolean
  disabled?: boolean
  onChange: (value: string) => void
  onBlur?: () => void
  onBlurTracking?: (value: string, isUnchanged: boolean) => void
  onFocus?: () => void
} & (
  | {
      labelTranslationKey?: never
      labelNode: React.ReactNode
    }
  | {
      labelTranslationKey: string
      labelNode?: never
    }
)

const ChipOptionField: React.FunctionComponent<ChipOptionFieldProps> = ({
  id,
  propertyName,
  option,

  options,
  translation,
  labelTranslationKey,
  errors,
  enableDeselection = true,
  isFirstChipFiltered = true,
  disabled = false,
  onChange,
  onBlur,
  onBlurTracking,
  onFocus,
  labelNode,
}: ChipOptionFieldProps) => {
  const { setTouched, formState } = useFormState()
  const showErrors = () =>
    errors && errors.length > 0 && (formState.submitCount > 0 || (propertyName && formState.touched[propertyName]))
  const { t } = useTranslation()

  const [state, setState] = useState<{
    isUnchanged: boolean
    lastValue: string | undefined
    currentValue: string | undefined
  }>({
    isUnchanged: true,
    lastValue: option,
    currentValue: option,
  })

  const chips: React.JSX.Element[] = []

  const doChange = (value: string) => {
    if (disabled) return
    const valueToSave = enableDeselection && value === option ? options[0] : value
    onChange(valueToSave)
    setState((prev) => ({ ...prev, currentValue: valueToSave }))
  }

  const onLostFocus = ({ relatedTarget }: React.FocusEvent<HTMLElement>) => {
    onBlur && onBlur()
    propertyName && setTouched(propertyName)
    if (!relatedTarget?.closest(`#${id}`) && state.currentValue && state.lastValue !== state.currentValue) {
      onBlurTracking && onBlurTracking(state.currentValue, state.isUnchanged)
      setState((prev) => ({ ...prev, lastValue: state.currentValue, isUnchanged: false }))
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>, value: string) => {
    if (event.key === KEY_ENTER || event.code === KEY_SPACE) {
      doChange(value)
      event.preventDefault()
    }
  }

  options.forEach((value) => {
    const lowerValue = id + '-' + underlineToDash(value)
    const label = t(translation + camelCase(value), lowerValue.replace('_', ' '))
    chips.push(
      <React.Fragment key={lowerValue}>
        <div className={`grid-item`}>
          <label aria-label={label} htmlFor={lowerValue}>
            <div
              role='radio'
              aria-checked={option === value}
              aria-labelledby={lowerValue}
              className={`chip${option === value ? ' chip--active' : ''}${disabled ? ' chip--disabled' : ''}`}
              {...(onFocus && { onFocus })}
            >
              <input
                type='radio'
                id={lowerValue}
                name={`${lowerValue[0].toUpperCase()}${camelCase(lowerValue.slice(1))}Select`}
                className='hide'
                aria-hidden
                tabIndex={0}
                onClick={() => doChange(value)}
                disabled={disabled}
              />
              <span data-testid={lowerValue} tabIndex={0} onBlur={onLostFocus} onKeyDown={(e) => onKeyDown(e, value)}>
                {label}
              </span>
            </div>
          </label>
        </div>
      </React.Fragment>
    )
  })

  return (
    <>
      <InputLabel
        id={id}
        propertyName={propertyName}
        {...(labelTranslationKey ? { labelTranslationKey } : { labelNode })}
      />
      <div id={id} className='margin-top-xs'>
        <div className='grid-flex gutter-s' role='radiogroup' aria-labelledby={id + '-input-label'}>
          {isFirstChipFiltered ? chips.slice(1) : chips}
        </div>
        {showErrors() && (
          <div role='alert' aria-labelledby={id + '-input-label'} className='input-label-helper-text font-error'>
            <div className='caption caption--error' style={{ fontWeight: 'bold' }}>
              <IconS24CrossCircle24Filled className={'caption-icon'} />
              {t(`validation.${errors?.[0]}`)}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ChipOptionField
